import React, { useState, useEffect } from 'react';
import { Form } from '@ui/forms/form';
import { TextField } from '@common/ui/forms/input-field/text-field/text-field';
import { Button } from '@common/ui/buttons/button';
import { useForm } from 'react-hook-form';
import { CloseIcon } from '@ui/icons/material/Close';
import { toast } from '@ui/toast/toast';
import { message } from '@ui/i18n/message';
import { useAuth } from '@common/auth/use-auth';

interface Comment {
  id: number;
  user_id: number;
  username: string;
  comment_video: string;
  created_at: string;
}

interface CommentsDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  videoId: number;
}

interface CommentFormValues {
  comment: string;
}

interface TruncatedCommentProps {
  text: string;
  limit: number;
}

const TruncatedComment: React.FC<TruncatedCommentProps> = ({ text, limit }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const shouldTruncate = text.length > limit;

  const displayText = isExpanded ? text : shouldTruncate ? `${text.slice(0, limit)}...` : text;

  const toggleExpanded = (e: React.MouseEvent) => {
    e.preventDefault();
    setIsExpanded(!isExpanded);
  };

  if (!shouldTruncate) return <span>{text}</span>;

  return (
    <span>
      {displayText}
      <button
        onClick={toggleExpanded} style={{ color: '#959595' }} 
        className="ml-1 text-xs text-gray-400 hover:text-gray-500"
      >
        Ver {isExpanded ? 'menos' : 'mais'}
      </button>
    </span>
  );
};

const CommentsDrawer = ({ isOpen, onClose, videoId }: CommentsDrawerProps) => {
  const [comments, setComments] = useState<Comment[]>([]);
  const [loading, setLoading] = useState(false);
  const { user } = useAuth();
  const form = useForm<CommentFormValues>();
  const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;
  const commentLimit = isMobile ? 120 : 220;

  const fetchComments = async () => {
    if (!videoId) return;
    
    try {
      const response = await fetch(`/clips-comments.php?video_id=${videoId}`);
      if (!response.ok) {
        throw new Error('⚠️ Falha de rede');
      }
      const data = await response.json();
      if (data.status === 'success') {
        setComments(data.comments);
      } else {
        throw new Error(data.message || '😬 Falha ao buscar comentários');
      }
    } catch (error) {
      console.error('😬 Erro ao buscar comentários:', error);
      toast.danger(message('😬 Falha ao carregar comentários'));
    }
  };

  useEffect(() => {
    if (isOpen && videoId) {
      fetchComments();
    }
  }, [isOpen, videoId]);

  const onSubmit = async (values: CommentFormValues) => {
    if (!user) {
      toast.danger(message('⚠️ Por favor, faça login para comentar'));
      return;
    }

    if (!values.comment?.trim()) {
      toast.danger(message('⚠️ Por favor insira um comentário'));
      return;
    }

    setLoading(true);
    
    try {
      const response = await fetch('/clips-comments.php', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          user_id: user.id,
          id_video: videoId,
          comment_video: values.comment.trim(),
        }),
      });

      if (!response.ok) {
        throw new Error('😬 Erro ao conectar com a rede');
      }

      const result = await response.json();

      if (result.status === 'success') {
        form.reset();
        await fetchComments();
        toast.positive(message('💬 Comentário postado com sucesso'));
      } else {
        throw new Error(result.message || '😭 Falha ao postar comentário');
      }
    } catch (error) {
      console.error('Erro ao postar comentário:', error);
      const errorMessage = error instanceof Error ? error.message : '😭 Falha ao postar comentário';
      toast.danger(message(errorMessage));
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('pt-BR', {
      day: 'numeric',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit'
    }).format(date)
    .replace('.', '')
    .toLowerCase();
  };

  return (
    <div
      className={`fixed inset-0 z-50 transition-opacity duration-300 ${
        isOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'
      }`}
    >
      <div 
        className="absolute inset-0 bg-black/50"
        onClick={onClose}
      />
      
      <div 
        className={`absolute bottom-0 left-0 right-0 bg rounded-t-xl transition-transform duration-300 transform ${
          isOpen ? 'translate-y-0' : 'translate-y-full'
        } ${
          typeof window !== 'undefined' && window.innerWidth >= 768 
            ? 'md:w-[380px] md:right-auto md:h-[80vh]' 
            : 'h-[80vh]'
        }`} 
        style={{ bottom: '39px', left: '5px', right: '5px'}}
      >
        <div className="flex items-center justify-between p-4 border-b">
          <h2 className="text-lg font-semibold">Comentários</h2>
          <button 
            onClick={onClose}
            className="p-1 hover:bg-gray-100 rounded-full"
          >
            <CloseIcon />
          </button>
        </div>

        <div className="h-full flex flex-col">
          <div style={{ padding: '.55rem .95rem' }} className="flex-1 overflow-y-auto">
            {comments.length > 0 ? (
              comments.map((comment) => (
                <div key={comment.id} className="mb-6">
                  <div className="block">
                    <div className="inline">
                      <span className="font-bold text-sm">{comment.username} : </span>
                      <span className="text-sm">
                        <TruncatedComment 
                          text={comment.comment_video} 
                          limit={commentLimit}
                        />
                      </span>
                    </div>
                    <div style={{ color: '#8f8f8f' }} className="mt-1 text-xs text-gray-400">
                      {formatDate(comment.created_at)}
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <p className="text-center text-gray-500">Nenhum comentário ainda</p>
            )}
          </div>

          <div className="border-t p-4 bg">
            <Form
              form={form}
              onSubmit={onSubmit}
            >
              <div className="flex items-center gap-2">
                <TextField
                  {...form.register('comment', { required: true })}
                  placeholder="Adicione um comentário..."
                  className="flex-1"
                  maxLength={500}
                  autoComplete="off"
                />
                <Button
                  type="submit"
                  variant="flat"
                  color="primary"
                  disabled={loading || !form.watch('comment')?.trim()}
                  className="min-w-[80px]"
                >
                  {loading ? 'Publicando...' : 'Postar'}
                </Button>
              </div>
            </Form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CommentsDrawer;